// ManufacturingOS demo · Static fixture data.
// Every number here is authoritative. It must match everywhere it is displayed.

export const company = {
  name: "Meridian Surgical Systems",
  sites: [
    { id: "warsaw", label: "Warsaw, IN · Line 1" },
    { id: "memphis", label: "Memphis, TN · Line 2" },
  ],
};

export const briefing = {
  date: "Friday, May 23, 2026",
  generatedAt: "07:02",
  addressee: "J. Okafor, Chief Operating Officer",
  addresseeShort: "J. Okafor",
  sources: ["SAP", "MasterControl", "Supplier correspondence"],
};

// ─────────────────────────────────────────────────────────────────────────────
// Original crisis fixture (Screens 1 & 2)
// ─────────────────────────────────────────────────────────────────────────────

export const moved = [
  {
    id: "moved-1",
    title: "Memphis Line 2 cleared backlog",
    detail: "340 units ahead of plan for May.",
  },
  {
    id: "moved-2",
    title: "PPAP P-2201 · tibial baseplate rev C",
    detail: "Castellan Metalworks · APPROVED · supplier cleared to ship.",
  },
];

export const crisis = {
  supplier: "Apex Precision Machining",
  po: "PO 4471",
  lot: "titanium locking plate lot",
  originalDate: "May 29, 2026",
  revisedDate: "June 12, 2026",
  detectionSource: "Apex email received May 14, 2026",
  emailSubject: "Weekly production update · Apex · wk20",
  emailHighlight:
    "Also flagging that the anodize rework queue pushes lot 4471-B roughly two weeks past the original commit, so we are targeting the week of June 8 for release.",
  workOrder: {
    id: "WO 8812",
    site: "Warsaw Line 1",
    scheduledStart: "June 1",
  },
  salesOrder: {
    id: "SO 30117",
    customer: "Northlake Health",
    committedDelivery: "June 5, 2026",
    orderValue: "$840,000",
    penaltyClause: "§7.2",
    penaltyTerms: "1.5% per week",
  },
};

export const recommendations = [
  {
    id: "rec-1",
    rank: "RECOMMENDED",
    title: "Split the order · expedite alternate lot",
    body: "Expedite alternate lot 4471-A (finished, awaiting anodize at Apex) via priority freight. Preserves June 5 delivery of 78% of SO 30117 units. Remaining 22% ships June 14.",
    metrics: [
      { label: "Freight cost", value: "$18,000" },
      { label: "On-time delivery", value: "78% of units" },
      { label: "Margin impact", value: "−2.1%" },
      { label: "Penalty exposure", value: "$0" },
    ],
    primary: true,
  },
  {
    id: "rec-2",
    rank: "ALTERNATE",
    title: "Notify Northlake · accept one-week slip",
    body: "Notify Northlake Health of a full one week slip to June 12. Relationship risk flagged: Northlake renewal Q4 2026.",
    metrics: [
      { label: "Freight cost", value: "$0" },
      { label: "New delivery date", value: "June 12" },
      { label: "Penalty exposure", value: "$12,600" },
      { label: "Clause", value: "§7.2" },
    ],
    primary: false,
  },
];

export const approval = {
  approver: "J. Okafor",
  action: "Split order + expedite freight",
  systemsAffected: ["SAP (PO 4471)", "SO 30117"],
  loggedTimestamp: "May 23 09:14 · J. Okafor",
};

export const ppap = {
  id: "P-2209",
  description: "locking screw assembly, rev D",
  supplier: "Castellan Metalworks",
  submitted: "May 21, 2026",
  elementsPresent: 18,
  elementsRequired: 22,
  defects: [
    {
      id: "d1",
      severity: "critical",
      title: "Dimensional results missing",
      detail: "Missing for 2 of 9 critical characteristics (CC-4, CC-7).",
      source: "View source · submission p.14",
    },
    {
      id: "d2",
      severity: "high",
      title: "Material certification references superseded spec",
      detail: "Cert references spec rev B; current released rev is C.",
      source: "View source · submission p.22",
    },
    {
      id: "d3",
      severity: "medium",
      title: "Control plan signature block incomplete",
      detail: "Quality manager signature absent.",
      source: "View source · submission p.31",
    },
    {
      id: "d4",
      severity: "medium",
      title: "Gauge R&R study exceeds validity window",
      detail: "Study dated March 2025; exceeds 12 month validity window.",
      source: "View source · submission p.38",
    },
  ],
  correctionRequest: {
    status: "Awaiting approval · R. Diaz, Quality Engineer",
    body:
      "Castellan team, package P-2209 review identified 4 items requiring correction before disposition: dimensional results for CC-4 and CC-7, material cert against spec rev C, completed control plan signatures, and a current gauge R&R study. Please resubmit by May 30 to hold the June production slot.",
  },
  banner:
    "Completeness findings only. This report does not constitute a regulatory determination.",
};

// ─────────────────────────────────────────────────────────────────────────────
// F1 · QMSR Readiness Crosswalk (Inspection Readiness Register)
// ─────────────────────────────────────────────────────────────────────────────

export const qmsr = {
  clauseMapVersion: "clause-map v2026.02",
  banner:
    "Findings only. This register does not constitute a determination of compliance.",
  summary: {
    critical: 2,
    high: 2,
    medium: 2,
    netChange: "+1 Critical since last briefing",
  },
  findings: [
    {
      id: "q1",
      clause: "ISO 13485 §7.5.6",
      clauseTitle: "Validation of processes for production and service provision",
      severity: "critical",
      driftClass: "evidence-gap",
      finding: "No mapped current document evidences process validation for anodize operation at supplier Apex Precision.",
      documentRef: null,
      source: { system: "MasterControl", record: "clause map v2026.02" },
      ageDays: 9,
      disposition: "open",
      remediation:
        "Draft SOP-VAL-014 revision covering anodize-line validation. Owner: T. Volkov. Requires supplier data package from Apex within 10 business days.",
      confidence: 0.94,
    },
    {
      id: "q2",
      clause: "ISO 13485 §8.5.3",
      clauseTitle: "Preventive action",
      severity: "critical",
      driftClass: "evidence-gap",
      finding: "Zero mapped current documents; preventive-action procedure marked obsolete May 2025 without replacement.",
      documentRef: null,
      source: { system: "MasterControl", record: "clause map v2026.02" },
      ageDays: 21,
      disposition: "open",
      remediation:
        "Reissue SOP-CAP-002 with preventive-action scope split out from CAPA. Owner: R. Diaz.",
      confidence: 0.97,
    },
    {
      id: "q3",
      clause: "ISO 13485 §8.3",
      clauseTitle: "Control of nonconforming product",
      severity: "high",
      driftClass: "evidence-gap",
      finding: "Partial evidence · referenced procedure retired without redirection.",
      documentRef: "SOP-8.3.2 rev 5 (retired)",
      source: { system: "MasterControl", record: "SOP-8.3.2 · rev 5" },
      ageDays: 4,
      disposition: "open",
      remediation:
        "Publish SOP-NCP-007 rev 1 to supersede SOP-8.3.2. Cross-link from clause register on release.",
      confidence: 0.88,
    },
    {
      id: "q4",
      clause: "ISO 13485 §7.4.2",
      clauseTitle: "Purchasing information · supplier control",
      severity: "high",
      driftClass: "evidence-gap",
      finding:
        "Risk-based supplier control evidence lapsed for Castellan Metalworks · quarterly review overdue by 42 days.",
      documentRef: "SUP-REV-CASTELLAN q4-2025",
      source: { system: "MasterControl", record: "SUP-REV-CASTELLAN" },
      ageDays: 2,
      disposition: "open",
      remediation:
        "Schedule Q1-2026 supplier review with R. Diaz and buyer M. Chen. Attach findings before May 31.",
      confidence: 0.92,
    },
    {
      id: "q5",
      clause: "ISO 13485 §4.2",
      clauseTitle: "Documentation requirements",
      severity: "medium",
      driftClass: "terminology",
      finding:
        "Procedure references retired QSR terminology \"Design History File\" instead of QMSR \"medical device file.\"",
      documentRef: "SOP-DES-001 rev 3",
      source: { system: "MasterControl", record: "SOP-DES-001 · rev 3" },
      ageDays: 12,
      disposition: "open",
      remediation:
        "Publish rev 4 with terminology replaced per lexicon v2026.02. No process change; documentation-only.",
      confidence: 0.99,
    },
    {
      id: "q6",
      clause: "ISO 13485 §7.4.1",
      clauseTitle: "Purchasing process",
      severity: "medium",
      driftClass: "terminology",
      finding:
        "Procedure cites superseded 21 CFR §820.50 language; QMSR §820.10 incorporates ISO 13485 §7.4.1 in place of it.",
      documentRef: "SOP-PUR-002 rev 7",
      source: { system: "MasterControl", record: "SOP-PUR-002 · rev 7" },
      ageDays: 6,
      disposition: "open",
      remediation:
        "Publish rev 8 with updated citations. Owner: M. Chen (Buyer).",
      confidence: 0.98,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// F2 · Commitment Risk Radar
// ─────────────────────────────────────────────────────────────────────────────

export const radar = {
  generatedAt: "May 23 06:58",
  summary: {
    total: 24,
    atRisk: 3,
    exposureTotal: "$19,300",
    netChange: "+1 at-risk since last briefing",
  },
  items: [
    {
      id: "r1",
      customer: "Northlake Health",
      salesOrder: "SO 30117",
      orderValue: "$840,000",
      committedDelivery: "June 5, 2026",
      exposure: "$12,600",
      exposureLabel: "Penalty exposure",
      runwayDays: 3,
      riskScore: 87,
      riskLabel: "High",
      recommendedAction: "Split order · expedite alternate lot",
      signals: [
        { label: "Supplier correspondence · Apex slip", weight: 0.42, source: "Apex email · May 14 · wk20" },
        { label: "PO revision history", weight: 0.24, source: "SAP · PO 4471 rev 2" },
        { label: "Work order slack negative", weight: 0.19, source: "SAP · WO 8812 vs. PO 4471" },
        { label: "Inventory position", weight: 0.15, source: "SAP · lot 4471-B qty 0" },
      ],
      linkToReasoning: true,
      isDemoScenario: true,
    },
    {
      id: "r2",
      customer: "Sentinel Regional",
      salesOrder: "SO 30402",
      orderValue: "$412,000",
      committedDelivery: "June 18, 2026",
      exposure: "exposure terms not on file",
      exposureLabel: "Contract terms",
      runwayDays: 8,
      riskScore: 71,
      riskLabel: "High",
      recommendedAction: "Confirm contract terms · escalate to CS",
      signals: [
        { label: "PO revision history · 2 revisions in 6 days", weight: 0.55, source: "SAP · PO 4482 rev 3" },
        { label: "Supplier correspondence · silence 11 days", weight: 0.25, source: "Riverbend Polymers thread" },
        { label: "Inventory position", weight: 0.20, source: "SAP · lot RP-4482 short 180 units" },
      ],
      linkToReasoning: false,
      isDemoScenario: false,
    },
    {
      id: "r3",
      customer: "Coastline Ortho",
      salesOrder: "SO 30588",
      orderValue: "$268,000",
      committedDelivery: "June 26, 2026",
      exposure: "$6,700",
      exposureLabel: "Penalty exposure",
      runwayDays: 14,
      riskScore: 58,
      riskLabel: "Medium",
      recommendedAction: "Reserve inventory from Memphis Line 2",
      signals: [
        { label: "Inventory position · required qty short 40 units", weight: 0.48, source: "SAP · lot CST-2201 qty 260" },
        { label: "Work order slack marginal", weight: 0.32, source: "SAP · WO 8903 vs. PO 4499" },
        { label: "PO revision history", weight: 0.20, source: "SAP · PO 4499 rev 1" },
      ],
      linkToReasoning: false,
      isDemoScenario: false,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// F3 · Living Supplier File (Watchlist)
// ─────────────────────────────────────────────────────────────────────────────

export const suppliers = {
  banner:
    "Findings only. This watchlist supports supplier management decisions and does not constitute a regulatory determination.",
  summary: {
    lapsed: 2,
    lapsingIn30: 1,
    lapsingIn90: 2,
    netChange: "+1 lapsed since last briefing",
  },
  ranking: [
    { supplier: "Apex Precision Machining", healthScore: 62, trend: "down" },
    { supplier: "Castellan Metalworks", healthScore: 71, trend: "flat" },
    { supplier: "Riverbend Polymers", healthScore: 84, trend: "up" },
    { supplier: "Ionica Coatings", healthScore: 91, trend: "flat" },
  ],
  watchlist: [
    {
      id: "s1",
      status: "lapsed",
      supplier: "Castellan Metalworks",
      document: "Gauge R&R study",
      clockClass: "R&R validity",
      dateLabel: "Dated March 2025 · 13 months old",
      affectedParts: ["locking screw assembly rev D", "tibial baseplate rev C"],
      openOrders: ["SO 30601"],
      source: { system: "MasterControl", record: "P-2209 · submission p.38" },
      draftStatus: "Awaiting approval · R. Diaz",
      draftBody:
        "Castellan team, our records show the gauge R&R study on file dated March 2025 has exceeded the 12-month validity window. Please submit a current study before June 6 to retain approved-supplier status for locking screw assembly rev D.",
    },
    {
      id: "s2",
      status: "lapsed",
      supplier: "Apex Precision Machining",
      document: "ISO 13485 certificate",
      clockClass: "Certificate expiration",
      dateLabel: "Expired May 8, 2026",
      affectedParts: ["titanium locking plate lot", "cortical screw shaft"],
      openOrders: ["SO 30117", "SO 30204"],
      source: { system: "Supplier File", record: "APX-CERT-2023-1105" },
      draftStatus: "Awaiting approval · R. Diaz",
      draftBody:
        "Apex team, our records show the ISO 13485:2016 certificate on file expired May 8, 2026. Please provide the current certificate or renewal evidence within 5 business days. Two open sales orders (SO 30117, SO 30204) touch parts sourced from your line.",
    },
    {
      id: "s3",
      status: "lapsing",
      supplier: "Riverbend Polymers",
      document: "Material certification",
      clockClass: "Certificate expiration",
      dateLabel: "Expires June 30, 2026 · 38 days",
      affectedParts: ["PEEK spacer body"],
      openOrders: ["SO 30402"],
      source: { system: "Supplier File", record: "RIV-MAT-2025-0630" },
      draftStatus: "Draft ready · not yet queued",
      draftBody:
        "Riverbend team, the material cert on file expires June 30, 2026. Please provide the renewal package by June 12 to avoid a hold on SO 30402.",
    },
    {
      id: "s4",
      status: "lapsing",
      supplier: "Ionica Coatings",
      document: "Gauge R&R study",
      clockClass: "R&R validity",
      dateLabel: "Expires August 12, 2026 · 82 days",
      affectedParts: ["coated locking pin"],
      openOrders: [],
      source: { system: "Supplier File", record: "ION-GRR-2025-0812" },
      draftStatus: "Draft ready · not yet queued",
      draftBody:
        "Ionica team, the R&R study on file expires August 12, 2026. Please schedule the renewal study by mid-July.",
    },
    {
      id: "s5",
      status: "lapsed",
      supplier: "Castellan Metalworks",
      document: "PPAP element aging",
      clockClass: "PPAP element policy",
      dateLabel: "P-2201 dated June 2024 · aging 335 days",
      affectedParts: ["tibial baseplate rev C"],
      openOrders: ["SO 30188"],
      source: { system: "MasterControl", record: "P-2201 · submission root" },
      draftStatus: "Held · aging policy under review",
      draftBody:
        "Castellan team, PPAP P-2201 elements are aging past our 12-month refresh policy. Please plan the resubmission window with buyer M. Chen.",
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// F4 · Loaner Kit Utilization Engine (Reallocation Memo)
// ─────────────────────────────────────────────────────────────────────────────

export const assets = {
  period: "Reporting period · April 22 to May 22, 2026",
  totalImpact: "$487,500",
  banner:
    "Recommendations only. Reallocation execution is authorized by the field-asset owner.",
  moveKits: [
    {
      id: "m1",
      assetIds: ["KIT-SPN-0142", "KIT-SPN-0148"],
      fromAccount: "Regional Community Hospital",
      toAccount: "Grandview Medical Center",
      rationale: "Zero cases at Regional Community in trailing 90 days · 11 spine cases forecast at Grandview in June.",
      bookValue: "$184,000",
      source: { system: "ERP · fixed assets", record: "KIT-SPN-0142, KIT-SPN-0148" },
    },
    {
      id: "m2",
      assetIds: ["KIT-CRP-0071"],
      fromAccount: "Northlake Health · West campus",
      toAccount: "Northlake Health · Downtown campus",
      rationale: "Utilization at 6% vs. 44% across campuses · single account move, no reprocessing required.",
      bookValue: "$88,500",
      source: { system: "ERP · fixed assets", record: "KIT-CRP-0071" },
    },
  ],
  recoverSets: [
    {
      id: "r1",
      assetIds: ["SET-TIB-0233"],
      account: "Cascade Ortho Group",
      overdueDays: 47,
      returnWindowDays: 30,
      bookValue: "$92,500",
      source: { system: "Kit management · CSV feed", record: "SET-TIB-0233 · shipped Apr 6" },
    },
  ],
  deferPurchases: [
    {
      id: "d1",
      description: "Q3 cross-pin instrument set procurement",
      figure: "$122,500",
      rationale: "Coverage achievable with recovered SET-TIB-0233 and idle KIT-CRP-0071 utilization uplift.",
      source: { system: "ERP · capital request", record: "CAPEX-2026-Q3-014" },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// F5 · Lot Genealogy Tracer (Containment Scope Report)
// ─────────────────────────────────────────────────────────────────────────────

export const trace = {
  seedNode: {
    label: "Supplier lot 4471-B",
    kind: "supplier-lot",
    supplier: "Apex Precision Machining",
    material: "titanium locking plate",
    source: { system: "SAP", record: "PO 4471 · lot 4471-B" },
  },
  runAt: "May 23 07:14",
  runBy: "J. Okafor",
  completeness: "incomplete",
  banner:
    "Scoping support only. Recall decisions, reportability determinations, and regulatory notifications are made by the manufacturer.",
  nodes: [
    {
      id: "n1",
      depth: 0,
      kind: "supplier-lot",
      label: "Supplier lot 4471-B",
      detail: "Apex Precision Machining · titanium locking plate",
      source: { system: "SAP", record: "PO 4471" },
    },
    {
      id: "n2",
      depth: 1,
      kind: "work-order",
      label: "WO 8812",
      detail: "Warsaw Line 1 · consumed lot 4471-B",
      source: { system: "MES", record: "MES-WO-8812" },
      edge: "consumed-in",
      edgeSource: "MES-WO-8812",
    },
    {
      id: "n3",
      depth: 2,
      kind: "finished-lot",
      label: "MSS-LP-2026-05-441",
      detail: "Locking plate assembly · 240 units",
      source: { system: "SAP", record: "FG lot MSS-LP-2026-05-441" },
      edge: "produced-by",
      edgeSource: "SAP · goods receipt 21001-05",
    },
    {
      id: "n4",
      depth: 3,
      kind: "sales-order",
      label: "SO 30117",
      detail: "Northlake Health · 240 units · committed June 5",
      source: { system: "SAP", record: "SO 30117 line 1" },
      edge: "shipped-on",
      edgeSource: "SAP · delivery pending",
      shippedStatus: "pending",
    },
    {
      id: "n5",
      depth: 1,
      kind: "work-order",
      label: "WO 8815",
      detail: "Warsaw Line 1 · consumed lot 4471-B",
      source: { system: "MES", record: "MES-WO-8815" },
      edge: "consumed-in",
      edgeSource: "MES-WO-8815",
    },
    {
      id: "n6",
      depth: 2,
      kind: "finished-lot",
      label: "MSS-LP-2026-05-442",
      detail: "Locking plate assembly · 60 units",
      source: { system: "SAP", record: "FG lot MSS-LP-2026-05-442" },
      edge: "produced-by",
      edgeSource: "SAP · goods receipt 21001-06",
    },
    {
      id: "n7",
      depth: 3,
      kind: "sales-order",
      label: "SO 30188",
      detail: "Valley Health · 60 units · shipped May 19, 2026",
      source: { system: "SAP", record: "SO 30188 line 1" },
      edge: "shipped-on",
      edgeSource: "SAP · delivery 8802031",
      shippedStatus: "shipped",
    },
  ],
  breaks: [
    {
      id: "b1",
      label: "Traceability break at MES-WO-8815 pre-shipment QC linkage",
      detail:
        "MES logged a partial lot split for WO 8815 that is not reflected in ERP goods receipt. Pre-shipment QC lot linkage cannot be established from source records.",
      betweenNodes: ["n5", "n6"],
    },
  ],
  scope: {
    salesOrdersAffected: 2,
    unitsShippedAlready: 60,
    unitsPending: 240,
    workOrdersAffected: 2,
    finishedLotsAffected: 2,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Audit Log · newest first · extended with entries from F1-F5
// ─────────────────────────────────────────────────────────────────────────────

export const auditLog = [
  {
    id: "a13",
    timestamp: "May 23 09:14",
    actor: "J. Okafor (COO)",
    isSystem: false,
    kind: "signature",
    action: "Signature: approved split order + expedite freight",
    detail: "step-up authentication verified",
  },
  {
    id: "a10",
    timestamp: "May 23 07:14",
    actor: "J. Okafor (COO)",
    isSystem: false,
    action: "Trace query executed · seed lot 4471-B",
    detail: "scope: 2 sales orders · 1 traceability break declared",
  },
  {
    id: "a9",
    timestamp: "May 23 07:08",
    actor: "SYSTEM",
    isSystem: true,
    action: "Supplier watchlist recomputed",
    detail: "2 lapsed, 2 lapsing-in-90 · +1 lapsed since last briefing",
  },
  {
    id: "a8",
    timestamp: "May 23 07:05",
    actor: "SYSTEM",
    isSystem: true,
    action: "Commitment risk radar recomputed",
    detail: "24 open commitments · 3 at-risk · exposure $19,300",
  },
  {
    id: "a7",
    timestamp: "May 23 07:04",
    actor: "SYSTEM",
    isSystem: true,
    action: "QMSR readiness register regenerated",
    detail: "clause-map v2026.02 · 2 Critical · 2 High · 2 Medium · +1 Critical net",
  },
  {
    id: "a1",
    timestamp: "May 23 07:02",
    actor: "SYSTEM",
    isSystem: true,
    action: "Daily briefing generated for J. Okafor",
    detail: "sources: SAP, MasterControl, supplier correspondence",
  },
  {
    id: "a2",
    timestamp: "May 23 06:58",
    actor: "SYSTEM",
    isSystem: true,
    action: "Risk flag raised: PO 4471 slip detected in Apex correspondence",
    detail: "confidence: high · reasoning trace stored",
  },
  {
    id: "a3",
    timestamp: "May 22 16:41",
    actor: "R. Diaz (Quality Engineer)",
    isSystem: false,
    action: "Approved PPAP P-2201 disposition",
    detail: "action logged, exported to MasterControl on explicit trigger",
  },
  {
    id: "a11",
    timestamp: "May 22 15:22",
    actor: "M. Chen (Buyer)",
    isSystem: false,
    action: "Executed reallocation · KIT-CRP-0071 moved (Northlake West → Downtown)",
    detail: "book value $88,500 · registry updated · SAP fixed-asset move logged",
  },
  {
    id: "a4",
    timestamp: "May 22 16:40",
    actor: "SYSTEM",
    isSystem: true,
    action: "PPAP P-2201 completeness report generated",
    detail: "22 of 22 elements present",
  },
  {
    id: "a12",
    timestamp: "May 21 14:03",
    actor: "R. Diaz (Quality Engineer)",
    isSystem: false,
    action: "Dispositioned QMSR finding · Clause §4.2 terminology drift",
    detail: 'rationale: "scheduled with SOP-DES-001 rev 4 for June release"',
  },
  {
    id: "a5",
    timestamp: "May 21 09:15",
    actor: "SYSTEM",
    isSystem: true,
    action: "PPAP P-2209 completeness report generated",
    detail:
      "18 of 22 elements present · correction request drafted, held for human approval",
  },
  {
    id: "a6",
    timestamp: "May 20 11:03",
    actor: "M. Chen (Buyer)",
    isSystem: false,
    action: "Declined recommendation: reorder point change for anchor pins",
    detail: 'reason logged: "seasonal demand, holding current"',
  },
  {
    id: "g1",
    timestamp: "May 20 09:12",
    actor: "SYSTEM",
    isSystem: true,
    kind: "grant",
    action: "Authority granted: production-commitment → J. Okafor (Executive · COO)",
    detail: "basis: role assignment · effective indefinite · IdP: Meridian Azure AD",
  },
  {
    id: "g2",
    timestamp: "May 20 09:12",
    actor: "SYSTEM",
    isSystem: true,
    kind: "grant",
    action: "Authority granted: quality-disposition → R. Diaz (Quality Engineer)",
    detail: "basis: role assignment · effective indefinite · IdP: Meridian Azure AD",
  },
  {
    id: "g3",
    timestamp: "May 18 14:20",
    actor: "SYSTEM",
    isSystem: true,
    kind: "revoke",
    action: "Authority revoked: production-commitment ← D. Powell (former VP Ops)",
    detail: "basis: role change · IdP directory sync",
  },
];
